import React from "react";

const ReactionEmoji = ({ emoji, label }) => (
  <span aria-label={label} role="img">
    {emoji}
  </span>
);

// Keep these Stream reaction type IDs aligned with the mobile client and the
// backend push-notification formatter. Explicit options prevent the web and
// native SDKs from choosing different defaults as their versions change.
export const CHAT_REACTION_OPTIONS = [
  {
    type: "like",
    Component: () => <ReactionEmoji emoji="👍" label="Like" />,
    name: "Like",
  },
  {
    type: "love",
    Component: () => <ReactionEmoji emoji="❤️" label="Love" />,
    name: "Love",
  },
  {
    type: "haha",
    Component: () => <ReactionEmoji emoji="😂" label="Haha" />,
    name: "Haha",
  },
  {
    type: "wow",
    Component: () => <ReactionEmoji emoji="😲" label="Wow" />,
    name: "Wow",
  },
  {
    type: "sad",
    Component: () => <ReactionEmoji emoji="😔" label="Sad" />,
    name: "Sad",
  },
];

// Stream reaction types must be plain identifiers, so emoji picked from the
// full picker are stored as "emoji_<codepoints>" and decoded for display.
export const EMOJI_REACTION_PREFIX = "emoji_";

export const encodeEmojiReactionType = (native) =>
  EMOJI_REACTION_PREFIX +
  Array.from(native || "")
    .map((char) => char.codePointAt(0).toString(16))
    .join("-");

export const decodeEmojiReactionType = (type) => {
  if (typeof type !== "string" || !type.startsWith(EMOJI_REACTION_PREFIX)) return null;
  try {
    const points = type
      .slice(EMOJI_REACTION_PREFIX.length)
      .split("-")
      .filter(Boolean)
      .map((hex) => parseInt(hex, 16));
    if (!points.length || points.length > 12) return null;
    // reject NaN, out-of-range, control chars and lone surrogates
    if (
      points.some(
        (p) =>
          Number.isNaN(p) || p < 0x20 || p > 0x10ffff || (p >= 0xd800 && p <= 0xdfff)
      )
    ) {
      return null;
    }
    return String.fromCodePoint(...points);
  } catch (error) {
    return null;
  }
};

const QUICK_REACTION_TYPES = new Set(CHAT_REACTION_OPTIONS.map((option) => option.type));

// Any emoji can be a reaction (its type is the emoji itself). Stream only
// renders reaction types it knows about, so build the option list from the
// quick reactions plus every extra emoji already used in the channel.
export const collectExtraReactionTypes = (channel) => {
  const extra = new Set();
  const visit = (message) => {
    const groups = message?.reaction_groups || {};
    Object.keys(groups).forEach((type) => {
      if (
        !QUICK_REACTION_TYPES.has(type) &&
        groups[type]?.count > 0 &&
        decodeEmojiReactionType(type)
      ) {
        extra.add(type);
      }
    });
    (message?.latest_reactions || []).forEach((reaction) => {
      if (
        reaction?.type &&
        !QUICK_REACTION_TYPES.has(reaction.type) &&
        decodeEmojiReactionType(reaction.type)
      ) {
        extra.add(reaction.type);
      }
    });
  };
  (channel?.state?.messages || []).forEach(visit);
  Object.values(channel?.state?.threads || {}).forEach((thread) =>
    (thread || []).forEach(visit)
  );
  return Array.from(extra);
};

export const buildReactionOptions = (extraTypes = []) => [
  ...CHAT_REACTION_OPTIONS,
  ...extraTypes.map((type) => {
    const emoji = decodeEmojiReactionType(type) || type;
    return {
      type,
      name: emoji,
      Component: () => <ReactionEmoji emoji={emoji} label={emoji} />,
    };
  }),
];
